import { useEffect } from 'react';
import type { BlogPost } from '../api/blog';
import { formatSlug } from '../api/blog';

// Single source of truth for page metadata and structured data. Used at runtime by
// usePageMeta and at build time by scripts/prerender.tsx, which writes it into the
// static HTML that crawlers (including AI crawlers that don't run JavaScript) read.

export const SITE_URL = 'https://cognitaix.com';
export const SITE_NAME = 'CognitAIX';
export const ORGANIZATION_NAME = 'CognitAIX Innovations';
export const PRODUCT_NAME = 'WebAIExcel';
export const OG_IMAGE = `${SITE_URL}/og-image.png`;
export const LOGO_URL = `${SITE_URL}/logo512.png`;
export const SAME_AS = [
  'https://www.linkedin.com/company/cognitaix',
  'https://www.youtube.com/@CognitAIX-WebAIExcel',
];

// Directory-style URLs: the build writes each page to <path>/index.html.
export const absoluteUrl = (path: string) => `${SITE_URL}${path === '/' ? '/' : `${path.replace(/\/$/, '')}/`}`;

export type JsonLd = Record<string, unknown>;

export type PageMeta = {
  path: string;
  title: string;
  description: string;
  keywords?: string[];
  type?: 'website' | 'article';
  image?: string;
  jsonLd?: JsonLd[];
};

export const FAQS = [
  {
    question: 'What is WebAIExcel?',
    answer:
      'WebAIExcel is an AI-powered Microsoft Excel add-in from CognitAIX. It brings AI, real-time web and database data, and workflow automation into Excel through custom task panes, rich custom data types and custom functions, tailored to each team’s data and processes.',
  },
  {
    question: 'How is WebAIExcel different from Microsoft Copilot?',
    answer:
      'Copilot is a general-purpose assistant. WebAIExcel is customized around your own data, processes and workflow challenges, and it uses smart automation instead of AI where automation is the better fit, so it removes the bottlenecks your team actually has.',
  },
  {
    question: 'Who is WebAIExcel for?',
    answer:
      'WebAIExcel is built for finance and healthcare professionals, and for risk management and operations teams, who rely on Excel for analysis, reporting and modeling.',
  },
  {
    question: 'Does WebAIExcel replace Excel?',
    answer:
      'No. WebAIExcel works inside Excel as a task-pane add-in. It enhances Excel without replacing it, so your team keeps its existing spreadsheets and ways of working.',
  },
  {
    question: 'Can WebAIExcel handle large datasets in Excel?',
    answer:
      'Yes. WebAIExcel offloads large datasets to a secure database for server-side processing and loads only the relevant, preprocessed data into Excel, reducing memory use, slow calculations and crashes.',
  },
  {
    question: 'Can I query my data in plain English?',
    answer:
      'Yes. WebAIExcel provides natural language access to web and database data, so you can ask questions in plain English and get answers in Excel without writing SQL or code.',
  },
  {
    question: 'Is WebAIExcel secure and audit-ready?',
    answer:
      'WebAIExcel supports role-based access control, versioning and audit logs so every change is tracked and recoverable, helping teams meet regulatory requirements such as Basel III, IFRS, SOX, FDA, EMA and MHRA.',
  },
  {
    question: 'How do we get started with WebAIExcel?',
    answer:
      'CognitAIX starts by understanding your workflow, then prototypes a solution inside your Excel and refines it with you before any commitment. Contact the team to schedule a demo.',
  },
];

const organization: JsonLd = {
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: ORGANIZATION_NAME,
  alternateName: SITE_NAME,
  url: `${SITE_URL}/`,
  logo: LOGO_URL,
  description:
    'CognitAIX builds WebAIExcel, an AI-powered Excel add-in that brings AI, real-time data and automation into Excel for finance and healthcare professionals.',
  sameAs: SAME_AS,
};

const website: JsonLd = {
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: `${SITE_URL}/`,
  name: SITE_NAME,
  publisher: { '@id': `${SITE_URL}/#organization` },
};

const softwareApplication: JsonLd = {
  '@type': 'SoftwareApplication',
  '@id': `${SITE_URL}/#webaiexcel`,
  name: PRODUCT_NAME,
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Microsoft Excel add-in',
  operatingSystem: 'Microsoft Excel',
  url: absoluteUrl('/webaiexcel'),
  description:
    'AI-powered Excel add-in with custom task panes, rich data types, custom functions, natural language data access and workflow automation for finance and healthcare teams.',
  featureList: [
    'Custom web-based task panes',
    'Rich custom data types',
    'Smart automation and custom functions',
    'Natural language access to web and database data',
    'Server-side processing for large datasets',
    'Role-based access control, versioning and audit logs',
  ],
  publisher: { '@id': `${SITE_URL}/#organization` },
};

const faqPage: JsonLd = {
  '@type': 'FAQPage',
  mainEntity: FAQS.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  })),
};

const breadcrumbs = (items: { name: string; path: string }[]): JsonLd => ({
  '@type': 'BreadcrumbList',
  itemListElement: [{ name: 'Home', path: '/' }, ...items].map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
});

const SOLUTION_NAMES = [
  'Enriched Data for Smarter Decisions',
  'Smarter Financial Analytics',
  'AI-Powered Compliance',
  'AI Agents for Automation',
  'EOD & Production Monitoring',
];

const podcastSeries: JsonLd = {
  '@type': 'PodcastSeries',
  '@id': `${SITE_URL}/blogs/#podcast`,
  name: 'WebAIExcel Podcast',
  url: absoluteUrl('/blogs'),
  description: 'Conversations on AI, automation and getting more out of Excel for finance and healthcare professionals.',
  publisher: { '@id': `${SITE_URL}/#organization` },
};

export const PAGES: Record<string, PageMeta> = {
  home: {
    path: '/',
    title: 'WebAIExcel by CognitAIX | AI-Powered Excel Add-in for Finance & Healthcare',
    description:
      'WebAIExcel brings AI, real-time data and automation into Microsoft Excel—customized for finance and healthcare workflows. Natural language data access, custom functions and more.',
    keywords: ['AI Excel add-in', 'AI-powered Excel', 'Excel automation', 'Excel AI for finance', 'Excel AI for healthcare'],
    jsonLd: [organization, website, softwareApplication],
  },
  solutions: {
    path: '/solutions',
    title: 'Excel AI Solutions for Finance Teams | WebAIExcel by CognitAIX',
    description:
      'AI-powered Excel solutions for financial analytics, compliance and ESG reporting, data enrichment, AI agents for automation and EOD production monitoring—built on WebAIExcel.',
    keywords: ['Excel AI solutions', 'financial analytics in Excel', 'compliance reporting automation', 'AI agents for finance', 'EOD monitoring'],
    jsonLd: [
      {
        '@type': 'ItemList',
        name: 'Solutions built on WebAIExcel',
        itemListElement: SOLUTION_NAMES.map((name, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: { '@type': 'Service', name, provider: { '@id': `${SITE_URL}/#organization` } },
        })),
      },
      breadcrumbs([{ name: 'Solutions', path: '/solutions' }]),
    ],
  },
  webaiexcel: {
    path: '/webaiexcel',
    title: 'WebAIExcel Demos & FAQ | AI Excel Add-in vs Copilot',
    description:
      'Watch WebAIExcel demos for finance and healthcare, see how it differs from Microsoft Copilot, and get answers to common questions about the AI-powered Excel add-in.',
    keywords: ['WebAIExcel demo', 'Copilot alternative for Excel', 'Excel add-in for finance', 'AI Excel FAQ'],
    jsonLd: [softwareApplication, faqPage, breadcrumbs([{ name: 'WebAIExcel', path: '/webaiexcel' }])],
  },
  blogs: {
    path: '/blogs',
    title: 'WebAIExcel Podcast & Blog | Insights on AI-Powered Excel',
    description:
      'Listen to the WebAIExcel podcast: conversations on AI, automation and getting more out of Excel for finance and healthcare professionals.',
    keywords: ['Excel AI podcast', 'AI in finance podcast', 'Excel automation insights'],
    jsonLd: [podcastSeries, breadcrumbs([{ name: 'Blog & Podcast', path: '/blogs' }])],
  },
  contact: {
    path: '/contact',
    title: 'Contact CognitAIX | Schedule a WebAIExcel Demo',
    description: 'Talk to CognitAIX about your Excel workflows and schedule a WebAIExcel demo for your finance or healthcare team.',
    jsonLd: [{ '@type': 'ContactPage', about: { '@id': `${SITE_URL}/#organization` } }, breadcrumbs([{ name: 'Contact', path: '/contact' }])],
  },
  about: {
    path: '/about',
    title: 'About CognitAIX | The Team Behind WebAIExcel',
    description:
      'CognitAIX is a team of finance and technology experts enhancing Excel with web and AI technology—without replacing it—through its flagship product, WebAIExcel.',
    jsonLd: [{ '@type': 'AboutPage', about: { '@id': `${SITE_URL}/#organization` } }, organization, breadcrumbs([{ name: 'About', path: '/about' }])],
  },
};

const postDate = (post: BlogPost) => post.created_at ?? post.updated_at ?? undefined;

export const podcastMeta = (post: BlogPost): PageMeta => {
  const path = `/blogs/podcast/${formatSlug(post.title)}`;
  return {
    path,
    title: `${post.title} | WebAIExcel Podcast`,
    description: `${post.title} — an episode of the WebAIExcel podcast on AI, automation and Excel for finance and healthcare professionals.`,
    type: 'article',
    image: post.content.image_url,
    jsonLd: [
      {
        '@type': 'PodcastEpisode',
        name: post.title,
        url: absoluteUrl(path),
        datePublished: postDate(post),
        image: post.content.image_url,
        associatedMedia: { '@type': 'AudioObject', contentUrl: post.content.audio_url },
        partOfSeries: { '@id': `${SITE_URL}/blogs/#podcast` },
      },
      breadcrumbs([
        { name: 'Blog & Podcast', path: '/blogs' },
        { name: post.title, path },
      ]),
    ],
  };
};

export const articleMeta = (post: BlogPost): PageMeta => {
  const path = `/blogs/blog/${formatSlug(post.title)}`;
  const text = post.content.blog_text ?? '';
  return {
    path,
    title: `${post.title} | CognitAIX Blog`,
    description: text ? `${text.slice(0, 155).trim()}…` : `${post.title} — from the CognitAIX blog.`,
    type: 'article',
    image: post.content.image_url,
    jsonLd: [
      {
        '@type': 'BlogPosting',
        headline: post.title,
        url: absoluteUrl(path),
        datePublished: postDate(post),
        image: post.content.image_url,
        publisher: { '@id': `${SITE_URL}/#organization` },
      },
      breadcrumbs([
        { name: 'Blog & Podcast', path: '/blogs' },
        { name: post.title, path },
      ]),
    ],
  };
};

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Head tags for a page, as an HTML string. The prerender script writes these into the
// static HTML; usePageMeta applies the same tags at runtime when the route changes.
export const renderHeadTags = (meta: PageMeta) => {
  const url = absoluteUrl(meta.path);
  const image = meta.image ?? OG_IMAGE;
  const tags = [
    `<title>${escapeHtml(meta.title)}</title>`,
    `<meta name="description" content="${escapeHtml(meta.description)}">`,
    meta.keywords ? `<meta name="keywords" content="${escapeHtml(meta.keywords.join(', '))}">` : '',
    `<link rel="canonical" href="${url}">`,
    `<meta property="og:site_name" content="${SITE_NAME}">`,
    `<meta property="og:type" content="${meta.type ?? 'website'}">`,
    `<meta property="og:title" content="${escapeHtml(meta.title)}">`,
    `<meta property="og:description" content="${escapeHtml(meta.description)}">`,
    `<meta property="og:url" content="${url}">`,
    `<meta property="og:image" content="${image}">`,
    `<meta name="twitter:card" content="summary_large_image">`,
    `<meta name="twitter:title" content="${escapeHtml(meta.title)}">`,
    `<meta name="twitter:description" content="${escapeHtml(meta.description)}">`,
    `<meta name="twitter:image" content="${image}">`,
  ];
  if (meta.jsonLd?.length) {
    const graph = { '@context': 'https://schema.org', '@graph': meta.jsonLd };
    tags.push(`<script type="application/ld+json">${JSON.stringify(graph).replace(/</g, '\\u003c')}</script>`);
  }
  return tags.filter(Boolean).join('\n    ');
};

const MANAGED_SELECTOR =
  'title, meta[name="description"], meta[name="keywords"], link[rel="canonical"], meta[property^="og:"], meta[name^="twitter:"], script[type="application/ld+json"]';

export const usePageMeta = (meta: PageMeta | undefined) => {
  const headTags = meta ? renderHeadTags(meta) : null;
  useEffect(() => {
    if (!headTags) return;
    // Replaces the prerendered tags (or the previous page's) with this page's.
    document.head.querySelectorAll(MANAGED_SELECTOR).forEach((el) => el.remove());
    const template = document.createElement('template');
    template.innerHTML = headTags;
    // Scripts parsed via innerHTML don't execute; JSON-LD only needs to be present.
    Array.from(template.content.children).forEach((el) => document.head.appendChild(el));
  }, [headTags]);
};
